import { useCallback, useEffect, useState } from 'react'
import { Switch } from '@headlessui/react'
import { toast } from 'react-toastify'

import { Button } from 'components/Buttons'
import { InvoiceConfig } from 'models'
import { classNames } from 'utils'
import { useViewClient } from 'modules/Clients/hooks/useViewClient'
import { editInvoiceConfig } from 'modules/Clients/services/update'

const TOGGLES: { key: keyof InvoiceConfig; label: string }[] = [
  { key: 'software', label: 'Software' },
  { key: 'hardware_license', label: 'Hardware (assinatura)' },
  { key: 'hardware_purchase', label: 'Hardware (compra/parcelas)' },
  { key: 'hardware_fines', label: 'Multas de hardware' },
  { key: 'connectivity', label: 'Conectividade (chips)' },
  { key: 'connectivity_fines', label: 'Multas de conectividade' }
]

export function BillingTab() {
  const { client, fetchClient, setClient } = useViewClient()

  const [isSaving, setIsSaving] = useState(false)
  const [invoiceConfig, setInvoiceConfig] = useState<InvoiceConfig | undefined>(
    undefined
  )

  useEffect(() => {
    if (!client) {
      fetchClient()
    }
  }, [client, fetchClient])

  useEffect(() => {
    if (client?.attributes.meta?.invoice_config) {
      setInvoiceConfig(client.attributes.meta.invoice_config)
    }
  }, [client])

  const onToggle = useCallback((key: keyof InvoiceConfig, value: boolean) => {
    setInvoiceConfig((prev) => ({
      ...(prev ?? ({} as InvoiceConfig)),
      [key]: value
    }))
  }, [])

  const onSave = useCallback(async () => {
    if (!client || !invoiceConfig) return
    setIsSaving(true)
    try {
      await editInvoiceConfig(client.id, invoiceConfig)
      setClient({
        ...client,
        attributes: {
          ...client.attributes,
          meta: { ...client.attributes.meta, invoice_config: invoiceConfig }
        }
      })
      toast.success('Configuração de cobranças salva com sucesso')
    } catch (error: any) {
      toast.error(
        error.suggestedMessage ?? 'Falha ao salvar configuração de cobranças'
      )
    } finally {
      setIsSaving(false)
    }
  }, [client, invoiceConfig, setClient])

  if (!invoiceConfig) return null

  return (
    <div className="w-1/2 px-4 mt-10 animate-fade-in-down">
      <div className="text-xl text-left leading-6 font-medium text-gray-900 mb-6">
        Configuração de cobranças
      </div>
      <div className="space-y-4">
        {TOGGLES.map(({ key, label }) => (
          <div
            key={key}
            className="flex items-center justify-between border-t border-gray-200 pt-4"
          >
            <span className="text-sm font-medium text-gray-900">{label}</span>
            <Switch
              checked={invoiceConfig[key]}
              onChange={(value) => onToggle(key, value)}
              className={classNames(
                invoiceConfig[key] ? 'bg-emerald-600' : 'bg-gray-200',
                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'
              )}
            >
              <span
                aria-hidden="true"
                className={classNames(
                  invoiceConfig[key] ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                )}
              />
            </Switch>
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-end">
        <Button
          type="button"
          disabled={isSaving}
          label="Salvar"
          onClick={onSave}
        />
      </div>
    </div>
  )
}
